import { defineMessages } from 'react-intl-next';

import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n.ts';

const i18nBase = defineMessages({
	header: {
		id: 'advanced-search.header',
		defaultMessage: 'Search',
		description: 'Page header title displayed at the top of the advanced search page',
	},
	textFieldLabel: {
		id: 'advanced-search.textfield.label',
		defaultMessage: 'Search in Confluence',
		description:
			'The text is shown as the label for the main free-text search input field on the advanced search page.',
	},
	textFieldPlaceholder: {
		id: 'advanced-search.textfield.placeholder',
		defaultMessage: 'Enter keywords to find pages, attachments, and more',
		description: 'Placeholder for the main free text search',
	},
	searchIconLabel: {
		id: 'advanced-search.textfield.search.icon.label',
		defaultMessage: 'Search icon',
		description: 'The aria-label for the magnifying glass icon in the search bar',
	},
	crossIconLabel: {
		id: 'advanced-search.textfield.cross.icon.label',
		defaultMessage: 'Clear search bar',
		description: 'The aria-label for the cross icon in the search bar',
	},
	clearFilterButtonText: {
		id: 'advanced-search.clear.filter.button.text',
		defaultMessage: 'Clear all',
		description: 'Button text that appears on the clear filters button to reset all search filters',
	},
	skipToSearchField: {
		id: 'advanced-search.search.skip.text.link',
		defaultMessage: 'Jump back to search field',
		description: 'Text for the hidden skip link to the search field',
	},
	landingPageHeader: {
		id: 'advanced-search.search.result.landing.page.header',
		defaultMessage: 'Find what you need in Confluence',
		description:
			'The text is shown on a hidden skip navigation link that returns keyboard focus to the search input field at the top of the advanced search page.',
	},
	landingPageText: {
		id: 'advanced-search.search.result.landing.page.text',
		defaultMessage: 'Ask a question and get an answer in search',
		description:
			'The text is shown as a heading on the landing state of the search results area when no query has been entered yet.',
	},
	resultNoResultHeader: {
		id: 'advanced-search.search.result.no.result.header',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)

		defaultMessage: "We couldn't find any matches for your search.",
		description: "Text when no search result is returned for 'filterText'",
	},
	resultNoResultSuggestion: {
		id: 'advanced-search.search.result.no.result.suggestion',
		defaultMessage: 'Try different keywords, fewer of them, or ones that are more general.',
		description: 'Suggestion in case of no results in the search results',
	},
	resultLastModifiedContent: {
		id: 'advanced-search.search.result.content.lastmodified',
		defaultMessage: 'Updated',
		description: 'The time when the content was modified last',
	},
	resultLastModifiedAttachment: {
		id: 'advanced-search.search.result.attachment.lastmodified',
		defaultMessage: 'Uploaded',
		description: 'The time when the attachment was uploaded',
	},
	searchFilterSpace: {
		id: 'advanced-search.filter.space.label',
		defaultMessage: 'Space',
		description:
			'Label for the filter that allows users to filter search results based on specific Confluence spaces',
	},
	searchFilterContentUnderAncestor: {
		id: 'advanced-search.filter.content.ancestor.label',
		defaultMessage: 'Content under',
		description: 'Search for content organized under the selected ancestor (page tree heirarchy)',
	},
	searchFilterMore: {
		id: 'advanced-search.filter.more.label',
		defaultMessage: 'More filters',
		description: 'Label for uncategorized filters that are not shown by default',
	},
	searchFilterArchivedContent: {
		id: 'advanced-search.filter.archivedContent.label',
		defaultMessage: 'Archived content',
		description: 'Filter based on including archived content',
	},
	searchContentFilterAncestorPlaceholder: {
		id: 'advanced-search.filter.ancestor.placeholder',
		defaultMessage: 'Select content nested under',
		description: 'Place holder for ancestor filter search input',
	},
	searchFilterAncestorRecentPages: {
		id: 'advanced-search.filter.ancestor.recentpages',
		defaultMessage: 'Recent pages',
		description:
			'Label for the group that displays recently viewed or modified pages in the ancestor filter',
	},
	searchFilterContributor: {
		id: 'advanced-search.filter.contributor.label',
		defaultMessage: 'Edited or created by',
		description:
			'Label for the filter that allows users to filter search results based on who edited or created the content',
	},
	searchFilterLabels: {
		id: 'advanced-search.filter.labels.label',
		defaultMessage: 'Labels',
		description:
			'Label for the filter that allows users to filter search results based on content labels',
	},
	searchFilterType: {
		id: 'advanced-search.filter.type.label',
		defaultMessage: 'Type',
		description:
			'The text is shown as the label for the filter drop-down that allows users to filter search results by content label.',
	},
	searchFilterVerified: {
		id: 'advanced-search.filter.verified.label',
		defaultMessage: 'Verified',
		description:
			'The text is shown as the label for the filter drop-down that allows users to filter search results by content type (e.g. pages, blogs, attachments).',
	},
	typePlaceholder: {
		id: 'advanced-search.type.placeholder',
		defaultMessage: 'Filter by type',
		description: 'The placeholder string for the type filter',
	},
	typePage: {
		id: 'advanced-search.type.page',
		defaultMessage: 'Pages',
		description: 'Label for the page content type option in the type filter',
	},
	typeBlogpost: {
		id: 'advanced-search.type.blogpost',
		defaultMessage: 'Blog posts',
		description: 'Label for the blogpost content type option in the type filter',
	},
	typeAttachment: {
		id: 'advanced-search.type.attachment',
		defaultMessage: 'Attachments',
		description: 'Label for the attachment content type option in the type filter',
	},
	typeSpace: {
		id: 'advanced-search.type.space',
		defaultMessage: 'Spaces',
		description: 'Label for the space content type option in the type filter',
	},
	typeWhiteboard: {
		id: 'advanced-search.type.whiteboard',
		defaultMessage: 'Whiteboards',
		description: 'Label for the whiteboard content type option in the type filter',
	},
	typeDatabase: {
		id: 'advanced-search.type.database',
		defaultMessage: 'Databases',
		description: 'Label for the database content type option in the type filter',
	},
	typeSlide: {
		id: 'advanced-search.type.slide',
		defaultMessage: 'Slides',
		description: 'Label for the slide content type option in the type filter',
	},
	typeEmbed: {
		id: 'advanced-search.type.embed',
		defaultMessage: 'Smart Links',
		description: 'Label for the Smart Link Embed content type option in the type filter',
	},
	typeComments: {
		id: 'advanced-search.type.comments',
		defaultMessage: 'Comments',
		description: 'Label for the comments content type option in the type filter',
	},
	typeFolder: {
		id: 'advanced-search.type.folders',
		defaultMessage: 'Folders',
		description: 'Label for the folders content type option in the type filter',
	},
	searchFilterTitle: {
		id: 'advanced-search.filter.title.label',
		defaultMessage: 'Search titles only',
		description:
			'The text is shown as a drop-down item in the content type filter representing the Folders content type on the advanced search page.',
	},
	searchFilterArchived: {
		id: 'advanced-search.filter.archived.label',
		defaultMessage: 'Show archived content',
		description: 'Include archived content in search results',
	},
	paginationLoadMoreButton: {
		id: 'advanced-search.load.more.text',
		defaultMessage: 'Load more',
		description: 'Label of button that, when clicked, loads the next set of search results',
	},
	spaceFilterPlaceholder: {
		id: 'advanced-search.space.filter.placeholder',
		defaultMessage: 'Find a space',
		description: 'Placeholder for input that filters results by space',
	},
	userFilterPlaceholder: {
		id: 'advanced-search.user.filter.placeholder',
		defaultMessage: 'Find a person',
		description: 'Placeholder for input that filters results by user',
	},
	labelFilterPlaceholder: {
		id: 'advanced-search.label.filter.placeholder',
		defaultMessage: 'Find a label',
		description: 'Placeholder for input that filters results by label',
	},
	ancestorFilterPlaceholder: {
		id: 'advanced-search.label.content.placeholder',
		defaultMessage: 'Search content',
		description: 'Placeholder for input that filters results by ancestor',
	},
	verifiedFilterPlaceholder: {
		id: 'advanced-search.verified.filter.placeholder',
		defaultMessage: 'Find verified content',
		description: 'Placeholder for input that filters results by verified status',
	},
	numberOfResults: {
		id: 'advanced-search.number.results',
		defaultMessage: 'Showing {numberOfResults, number} of {totalCount, number} results',
		description: 'Number of results the user is seeing out of the total number of results',
	},
	searchFilterLabelsNoText: {
		id: 'advanced-search.labels.filter.no.text',
		defaultMessage: 'Start typing to search for a label',
		description: "Text displayed when the user hasn't begun typing a label",
	},
	dateFilterTitle: {
		id: 'advanced-search.date.filter.title',
		defaultMessage: 'Last updated',
		description:
			"The text is shown as an empty-state message inside the labels filter drop-down when the user hasn't typed any input yet, prompting them to start typing.",
	},
	dateFilterFromTitle: {
		id: 'advanced-search.date.filter.from.title',
		defaultMessage: 'From',
		description:
			'The text is shown as the label for the date filter section heading that allows users to filter content by the date it was last updated.',
	},
	dateFilterToTitle: {
		id: 'advanced-search.date.filter.to.title',
		defaultMessage: 'To',
		description:
			'Label for the end date field that allows users to filter content up to a custom date',
	},
	dateFilterDateInputPlaceholder: {
		id: 'advanced-search.date.filter.input.placeholder',
		defaultMessage: 'Choose a date',
		description:
			'The text is shown as the label for the end date field in the custom date range filter, indicating the upper bound of the date range.',
	},
	dateFilterAnyTime: {
		id: 'advanced-search.date.filter.any.time',
		defaultMessage: 'Anytime',
		description:
			'The text is shown as placeholder text inside the date input fields in the custom date range filter on the advanced search page.',
	},
	dateFilterToday: {
		id: 'advanced-search.date.filter.today',
		defaultMessage: 'Today',
		description:
			'The text is shown as a drop-down item in the last-updated date filter representing the option to show content modified at any time (no date restriction).',
	},
	dateFilterYesterday: {
		id: 'advanced-search.date.filter.yesterday',
		defaultMessage: 'Yesterday',
		description: 'Date range of content modified yesterday',
	},
	dateFilterLastWeek: {
		id: 'advanced-search.date.filter.last.week',
		defaultMessage: 'Past 7 days',
		description: 'Date range of content modified in the last 7 days',
	},
	dateFilterLastMonth: {
		id: 'advanced-search.date.filter.last.month',
		defaultMessage: 'Past 30 days',
		description: 'Date range of content modified in the last 30 days',
	},
	dateFilterLastYear: {
		id: 'advanced-search.date.filter.last.year',
		defaultMessage: 'Past year',
		description: 'Date range of content modified in the last 365 days',
	},
	dateFilterCustom: {
		id: 'advanced-search.date.filter.custom',
		defaultMessage: 'Custom',
		description:
			'Option that allows users to specify a custom date range for filtering search results',
	},
	dateFilterCustomInvalidDateAfterToday: {
		id: 'advanced-search.date.filter.custom.invalid.after.today',
		defaultMessage: 'The From date can’t be in the future',
		description: 'error displayed when a date range begins after the current date',
	},
	dateFilterCustomInvalidToDateAfterToday: {
		id: 'advanced-search.date.filter.custom.invalid.to.after.today',
		defaultMessage: 'The To date can’t be in the future',
		description: 'error displayed when a date range ends after the current date',
	},
	dateFilterCustomInvalidDateAfterEnd: {
		id: 'advanced-search.date.filter.custom.invalid.after.end',
		defaultMessage: 'The From date has to be before the To date',
		description: 'error displayed when a date range begins after the end date',
	},
	dateFilterToRange: {
		id: 'advanced-search.date.filter.to.range',
		defaultMessage: 'to',
		description: "'to' in between from and to date pickers",
	},
	dateFilterUpdateButton: {
		id: 'advanced-search.date.filter.update.button',
		defaultMessage: 'Update',
		description: 'Text for update button in date filter picker',
	},
	dateFilterBeforeLabel: {
		id: 'advanced-search.date.filter.before.label',
		defaultMessage: 'before',
		description:
			'The text is shown on the button that applies/updates the selected custom date range in the date filter picker on the advanced search page.',
	},
	dateFilterAfterLabel: {
		id: 'advanced-search.date.filter.after.label',
		defaultMessage: 'after',
		description:
			'The text is shown as a label in the date picker drop-down indicating content updated before a selected date.',
	},
	selectorErrorMessage: {
		id: 'advanced-search.filter.generic.error-message',
		defaultMessage: 'Something went wrong.',
		description: 'Generic error message if something goes wrong with a dropdown selector',
	},
	xpsErrorHeader: {
		id: 'advanced-search.search.result.xps.error.header',
		defaultMessage: 'We’re having trouble getting your results',
		description:
			'Header for error message when the search api fails. Appears on the advanced search page',
	},
	xpsErrorSuggestion: {
		id: 'advanced-search.search.result.xps.error.suggestion',
		defaultMessage: '{link} and try again.',
		description:
			"Suggestion in case the search api fails. {link} will always be 'Refresh the page' from advanced-search.search.result.xps.error.suggestion.link. Appears on the advanced search page",
	},
	xpsErrorSuggestionLink: {
		id: 'advanced-search.search.result.xps.error.suggestion.link',
		defaultMessage: 'Refresh the page',
		description: 'Suggestion in case of no results in the search results',
	},
	feedbackButton: {
		id: 'advanced-search.search.feedback.button',
		defaultMessage: 'Give feedback',
		description: 'Button text for opening feedback dialog/form',
	},
	selectedLabel: {
		id: 'advanced-search.search.selected.label',
		defaultMessage: 'Selected',
		description: 'Label for selected group of options in dropdown filter picker',
	},
	suggestedLabel: {
		id: 'advanced-search.search.suggested.label',
		defaultMessage: 'Suggested',
		description: 'Label for suggested group of options in dropdown filter picker',
	},
	resultsLabel: {
		id: 'advanced-search.search.results.label',
		defaultMessage: 'Results',
		description: 'Label for results matching input while searching for additional filter options',
	},
	recentLabel: {
		id: 'advanced-search.search.recent.label',
		defaultMessage: 'Recent',
		description: 'Label for recent group of options in dropdown filter picker',
	},
	disabledSearchTitleOnlyTooltipMessage: {
		id: 'advanced-search.search.disabled.search.title.only.tooltip',
		defaultMessage: 'To search by titles only, please input your search in the search bar.',
		description: 'Tooltip message for when search by titles only is disabled',
	},
	askAIButton: {
		id: 'advanced-search.search.askAI.button.text',
		defaultMessage: 'Ask AI',
		description:
			'Button text that appears on the Ask AI button to trigger Atlassian Intelligence search',
	},
	disabledAIButtonMessage: {
		id: 'advanced-search.search.askAI.disabled.button.message',
		defaultMessage: 'Ask a question in search to get an answer from Atlassian Intelligence',
		description: 'Tooltip message shown when the Ask AI button is disabled',
	},
	spaceIconLabel: {
		id: 'advanced-search.result.space.icon.label',
		defaultMessage: 'Space logo for {spaceName}',
		description: 'Aria label text for space logo in advanced search results',
	},
});

const i18nLiveDocs = defineMessages({
	searchFilterAncestorRecentPages: {
		id: 'advanced-search.filter.ancestor.recentpages.livedocs',
		defaultMessage: 'Recent pages and live docs',
		description: 'Updated version of advanced-search.filter.ancestor.recentpages',
	},

	typePage: {
		id: 'advanced-search.type.page.livedocs',
		defaultMessage: 'Pages and live docs',
		description: 'Updated version of advanced-search.type.page',
	},
});

export const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);
